import React, { useEffect, useState } from "react";
import White from "../../assets/icons/popup/white-apple.svg?react";
import Login from "../../assets/icons/popup/e-lotto-pcso-logo-square.svg?react";
import BgLatto from "../../assets/images/bg-latto.jpg";

export default function AppDownloadPopup({ isOpen, onClose }) {
  const [isVisible, setIsVisible] = useState(isOpen);
  const [isAnimating, setIsAnimating] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOSDevice, setIsIOSDevice] = useState(false);
  const [isAndroidDevice, setIsAndroidDevice] = useState(false);

  // Detect device types
  useEffect(() => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    const isAndroid = /Android/.test(navigator.userAgent);
    setIsIOSDevice(isIOS);
    setIsAndroidDevice(isAndroid);
  }, []);

  // Capture the PWA install prompt
  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      // Store the event so it can be triggered later
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  // Animation handling
  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      setTimeout(() => setIsAnimating(true), 10); // Delay to apply smooth transition
    } else {
      setIsAnimating(false);
      const timer = setTimeout(() => setIsVisible(false), 300); // Unmount after animation
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Handle PWA installation
  const handleInstallClick = async () => {
    if (deferredPrompt) {
      // Show the native install prompt
      deferredPrompt.prompt();
      // Wait for the user to respond to the prompt
      const { outcome } = await deferredPrompt.userChoice;
      console.log(`User response to the install prompt: ${outcome}`);
      // We no longer need the prompt. Clear it
      setDeferredPrompt(null);
      // Close the popup
      onClose();
    } else if (isIOSDevice) {
      // For iOS devices, show instructions
      alert("To install on iOS: tap the share button, then 'Add to Home Screen'");
      onClose();
    } else {
      // No install prompt available, app might already be installed or not installable
      console.log("The app cannot be installed or is already installed.");
      onClose();
    }
  };

  if (!isVisible) return null;

  return (
    <div
      className={`fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 font-poppins transition-opacity duration-300 ${
        isAnimating ? "opacity-100" : "opacity-0"
      }`}
    >
      <div
        className={`relative w-full max-w-sm rounded-3xl overflow-hidden transform transition-all duration-300 ease-in-out ${
          isAnimating ? "scale-100 opacity-100" : "scale-95 opacity-0"
        }`}
      >
        {/* Background with gradient and floating elements */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url('${BgLatto}'), linear-gradient(132.14deg, #0279F9 0%, #293CA8 40%, #D42F55 100%)`,
            backgroundBlendMode: "soft-light",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        ></div>

        {/* Content */}
        <div className="relative px-8 py-10 text-center">
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute right-4 top-4 text-white/80 hover:text-white transition-colors"
            aria-label="Close"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>

          {/* Logo */}
          <div className="mb-6 flex justify-center">
            {/* <img src={logo} alt="logo" /> */}
            <Login />
          </div>

          {/* Text */}
          <h2 className="text-white text-3xl font-medium mb-6">APP Download</h2>

          {/* Buttons */}
          <div className="space-y-2.5 text-base font-semibold">
            {isIOSDevice ? (
              <>
                <button 
                  className="bg-blue-600 hover:bg-blue-700 text-white py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2.5 mx-auto w-full"
                  onClick={handleInstallClick}
                >
                  ADD TO HOME SCREEN
                </button>
                <button 
                  className="bg-transparent border border-blue-600 hover:bg-blue-800 text-white py-3 px-6 rounded-lg transition-colors flex justify-center gap-2.5 mx-auto w-full"
                  onClick={onClose}
                >
                  CONTINUE TO WEBSITE
                </button>
              </>
            ) : isAndroidDevice ? (
              <>
                <button 
                  className="bg-blue-600 hover:bg-blue-700 text-white py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2.5 mx-auto w-full"
                  onClick={handleInstallClick}
                >
                  INSTALL APP
                </button>
                <button 
                  className="bg-transparent border border-blue-600 hover:bg-blue-800 text-white py-3 px-6 rounded-lg transition-colors flex justify-center gap-2.5 mx-auto w-full"
                  onClick={onClose}
                >
                  CONTINUE TO WEBSITE
                </button>
              </>
            ) : (
              <>
                <button 
                  className="bg-blue-600 hover:bg-blue-700 text-white py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2.5 mx-auto w-full"
                  onClick={handleInstallClick}
                >
                  INSTALL WEB APP
                </button>
                <button 
                  className="bg-transparent border border-blue-600 hover:bg-blue-800 text-white py-3 px-6 rounded-lg transition-colors flex justify-center gap-2.5 mx-auto w-full"
                  onClick={onClose}
                >
                  CONTINUE TO WEBSITE
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
